import { gql } from "@apollo/client";
import { useCreateRoleMutation } from "@src/features/roles/dialogs/NewRoleDialog.generated";
import RoleDialogBody from "@src/features/roles/dialogs/RoleDialogBody";
import { useToast } from "@src/lib/hooks/useToast";
import { useTracking } from "@src/features/app/hooks/useTracking";
import {
  ActionPermissionSetInput,
  GraphqlOperations,
  UserType,
} from "@src/types.generated";
import { launchConfetti } from "@src/lib/utils/confetti";
import { useRouter } from "next/router";
import { FC, useCallback, useMemo, useState } from "react";
import { Dialog } from "@src/lib/ui/Dialog";
import { getRoleDetailRoute } from "@src/features/users/lib/utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialInput?: { name?: string };
  navigateToRoleOnSave?: boolean;
  onRoleCreated?: (roleId: number) => void;
};

const NewRoleDialog: FC<Props> = ({
  open,
  onOpenChange,
  initialInput,
  navigateToRoleOnSave = true,
  onRoleCreated,
}) => {
  const router = useRouter();
  const [name, setName] = useState(initialInput?.name || "");
  const [roleUserType, setRoleUserType] = useState<UserType | undefined>();
  const [roleGroupIds, setRoleGroupIds] = useState<string[]>([]);
  const [directReportRoleIds, setDirectReportRoleIds] = useState<number[]>([]);
  const [permissions, setPermissions] = useState<ActionPermissionSetInput>({});
  const [clockInConfirmationRequired, setClockInConfirmationRequired] =
    useState(true);
  const [roleLevelV2, setRoleLevelV2] = useState<number | undefined>();
  const { addErrorToast } = useToast();
  const { trackEvent } = useTracking();
  const toggleClockInConfirmationRequired = useCallback(() => {
    setClockInConfirmationRequired((current) => !current);
  }, []);
  const createRoleDisabled = useMemo(
    () => !name || !roleUserType,
    [name, roleUserType],
  );
  const [createRoleMutation, { loading }] = useCreateRoleMutation({
    variables: {
      input: {
        name,
        roleUserType,
        permissions,
        roleGroupIds: { value: roleGroupIds },
        requireClockInConfirmation: clockInConfirmationRequired,
        directReportRoleIds: { value: directReportRoleIds },
        roleLevelV2,
      },
    },
    refetchQueries: [
      GraphqlOperations.Query.RolesList,
      GraphqlOperations.Query.GetRoles,
      GraphqlOperations.Query.RecentlyViewedRoles,
      GraphqlOperations.Query.GetRolesForSelect,
    ],
    onCompleted: (data) => {
      if (!data.createRole.success) {
        addErrorToast({
          data,
          callsite: "new_role_dialog",
        });
      } else {
        trackEvent("created_role", { id: data.createRole?.role?.id });

        if (data.createRole?.role?.id && onRoleCreated) {
          onRoleCreated(data.createRole.role.id);
        }

        onOpenChange(false);
        if (!onRoleCreated) {
          launchConfetti();
        }
        if (
          navigateToRoleOnSave &&
          data.createRole?.role?.id &&
          !onRoleCreated
        ) {
          const id = data.createRole.role.id;
          router.push(getRoleDetailRoute(id));
        }
      }
    },
    onError: (error) => {
      addErrorToast({
        ...error,
        callsite: "new_role_dialog",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <RoleDialogBody
        title="New Role"
        name={name}
        setName={setName}
        roleUserType={roleUserType}
        setRoleUserType={setRoleUserType}
        permissions={permissions}
        setPermissions={setPermissions}
        roleGroupIds={roleGroupIds}
        setRoleGroupIds={setRoleGroupIds}
        clockInConfirmationRequired={clockInConfirmationRequired}
        setClockInConfirmationRequired={setClockInConfirmationRequired}
        toggleClockInConfirmationRequired={toggleClockInConfirmationRequired}
        saveCopy="Create Role"
        onClose={() => {
          onOpenChange(false);
        }}
        onSave={createRoleMutation}
        loading={loading}
        saveDisabled={createRoleDisabled}
        directReportRoleIds={directReportRoleIds}
        setDirectReportRoleIds={setDirectReportRoleIds}
        roleLevelV2={roleLevelV2}
        setRoleLevelV2={setRoleLevelV2}
      />
    </Dialog>
  );
};

export default NewRoleDialog;

gql`
  mutation createRole($input: CreateRoleInput!) {
    createRole(input: $input) {
      success
      role {
        ...RoleWithPermissions
      }
    }
  }

  fragment RoleWithPermissions on Role {
    id
    name
    roleGroupMemberships {
      id
      roleGroup {
        id
        name
      }
    }
    roleActionPermissions {
      ...RoleActionPermissions
    }
    directReports {
      id
      name
    }
  }

  fragment ActionPermissionSet on ActionPermissionSet {
    id
    changeOrgSettings
    manageOrgIntegrations
    manageBilling
    inviteOthers
    createContent
    manageLibrary
    assignContent
    unassignContent
    updateAutomations
    verifySkills
    viewReporting
    sendMessages
    sendPosts
    editTrainees
    deactivateTrainees
    managePremiumContent
    promoteTrainees
    markAsComplete
    viewAllSubmittedTaskLists
    createChat
    sendPosts
  }

  fragment RoleActionPermissions on RoleActionPermissions {
    id
    roleUserType
    actionPermissions {
      ...ActionPermissionSet
    }
    requireClockInConfirmation
  }
`;
